import React from "react";
import { useLang } from "../i18n/LanguageContext";

/*
 * Side-view cinema room visualization for the projection simulator.
 * Math source of truth = backend /api/simulator (nominal 1.2 throw ratio);
 * geometry here mirrors the identical formula for smooth live dragging.
 */
const WIDTH_FACTOR = 16 / Math.sqrt(16 * 16 + 9 * 9); // ~0.87157
const INCH = 0.0254;

export const RoomVisual = ({ distanceM = 3, screenInch = 100, refDiagInch = null, className = "" }) => {
  const { t } = useLang();
  const W = 560, H = 330;
  const floorY = H - 52;
  const wallX = W - 62;
  const minX = 58;

  // live geometry (identical formula to backend reference)
  const imgWidthM = distanceM / 1.2;
  const diag = refDiagInch || Math.round(imgWidthM / WIDTH_FACTOR / INCH);
  const imgHm = diag * INCH * WIDTH_FACTOR * (9 / 16);
  const screenHm = screenInch * INCH * WIDTH_FACTOR * (9 / 16);

  const vScale = (floorY - 64) / 2.9;      // metres -> px (wall)
  const hScale = (wallX - minX - 48) / 6.2; // metres -> px (floor)

  const screenHpx = Math.min(floorY - 70, Math.max(36, screenHm * vScale));
  const imgHpx = Math.min(floorY - 58, Math.max(26, imgHm * vScale));
  const wallMidY = 56 + (floorY - 64) / 2;
  const screenTop = wallMidY - screenHpx / 2;
  const screenBot = screenTop + screenHpx;
  const imgTop = wallMidY - imgHpx / 2;
  const imgBot = imgTop + imgHpx;

  const projX = Math.max(minX, wallX - 30 - distanceM * hScale);
  const standTop = floorY - 30;
  const bodyY = standTop - 24;
  const lensX = projX + 46;
  const lensY = bodyY + 12;

  const fits = diag <= screenInch * 1.08;
  const spillTop = Math.max(0, screenTop - imgTop);
  const spillBot = Math.max(0, imgBot - screenBot);

  // dust motes along the beam
  const motes = [0.28, 0.46, 0.62, 0.8].map((f, i) => ({
    x: lensX + (wallX - lensX) * f,
    y: lensY + (imgTop + imgHpx * (0.3 + 0.15 * i) - lensY) * f,
    r: 1 + (i % 2) * 0.6,
  }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={`w-full h-auto ${className}`} role="img" aria-label="room projection preview" data-testid="room-visual">
      <defs>
        <linearGradient id="rv-room" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b0e15" /><stop offset="0.72" stopColor="#070910" /><stop offset="1" stopColor="#0a0c12" />
        </linearGradient>
        <linearGradient id="rv-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#10131b" /><stop offset="1" stopColor="#07080d" />
        </linearGradient>
        <linearGradient id="rv-beam" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(232,241,252,0.42)" />
          <stop offset="0.55" stopColor="rgba(232,241,252,0.14)" />
          <stop offset="1" stopColor="rgba(232,241,252,0.20)" />
        </linearGradient>
        <linearGradient id="rv-screen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#eef1f6" /><stop offset="1" stopColor="#c9d1db" />
        </linearGradient>
        <filter id="rv-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* room shell */}
      <rect x="0" y="0" width={W} height={H} rx="14" fill="url(#rv-room)" />
      <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="14" fill="none" stroke="rgba(255,255,255,0.08)" />
      {/* ceiling + wall + floor */}
      <line x1="22" y1="34" x2={wallX + 26} y2="34" stroke="rgba(255,255,255,0.05)" />
      <line x1={wallX + 26} y1="34" x2={wallX + 26} y2={floorY} stroke="rgba(255,255,255,0.10)" />
      <rect x="14" y={floorY} width={W - 28} height={H - floorY - 12} rx="4" fill="url(#rv-floor)" />
      <line x1="18" y1={floorY} x2={W - 18} y2={floorY} stroke="rgba(255,255,255,0.10)" />

      {/* wall-mounted screen (viewer's screen size) */}
      <rect x={wallX + 8} y={screenTop - 10} width="14" height="7" rx="2" fill="#1b202b" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8" />
      <rect className="rv-geo" x={wallX + 10} y={screenTop} width="9" height={screenHpx} rx="2" fill="url(#rv-screen)" stroke="rgba(4,6,10,0.8)" strokeWidth="1" />
      <rect x={wallX + 8} y={screenBot + 2} width="14" height="4" rx="1.5" fill="#141821" />

      {/* beam */}
      <polygon points={`${lensX},${lensY - 4} ${wallX + 10},${imgTop} ${wallX + 10},${imgBot} ${lensX},${lensY + 4}`} fill="url(#rv-beam)" opacity="0.9" />
      <polygon points={`${lensX},${lensY - 1.5} ${wallX + 10},${imgTop + imgHpx * 0.34} ${wallX + 10},${imgTop + imgHpx * 0.66} ${lensX},${lensY + 1.5}`} fill="rgba(240,247,255,0.28)" />
      {motes.map((m, i) => (
        <circle key={i} cx={m.x} cy={m.y} r={m.r} fill="rgba(240,247,255,0.55)" />
      ))}

      {/* projected image on the wall (light) */}
      <rect className="rv-geo" x={wallX + 6} y={imgTop} width="8" height={imgHpx} rx="1.5" fill="rgba(240,246,253,0.95)" filter="url(#rv-glow)" opacity="0.65" />
      <rect className="rv-geo" x={wallX + 10} y={imgTop} width="9" height={imgHpx} rx="2" fill="rgba(244,248,254,0.92)" />
      {/* spill beyond the screen when the image is too large */}
      {spillTop > 1 && <rect x={wallX + 9} y={imgTop} width="11" height={spillTop} rx="2" fill="rgba(212,168,67,0.85)" />}
      {spillBot > 1 && <rect x={wallX + 9} y={screenBot} width="11" height={spillBot} rx="2" fill="rgba(212,168,67,0.85)" />}

      {/* projector on stand (moves with distance) */}
      <g className="rv-anim" style={{ transform: `translateX(${projX}px)` }}>
        <ellipse cx="24" cy={floorY - 2} rx="30" ry="4" fill="rgba(0,0,0,0.5)" />
        <rect x="20" y={standTop} width="7" height={floorY - standTop - 2} rx="2" fill="#141821" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" />
        <rect x="6" y={floorY - 6} width="36" height="4" rx="2" fill="#10131b" stroke="rgba(255,255,255,0.07)" strokeWidth="0.6" />
        <rect x="0" y={bodyY} width="46" height="24" rx="6" fill="#1a1e28" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        <rect x="4" y={bodyY + 2} width="38" height="2" rx="1" fill="rgba(226,238,252,0.28)" />
        <circle cx="46" cy={bodyY + 12} r="5.5" fill="#0b0d13" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        <circle cx="46" cy={bodyY + 12} r="3" fill="rgba(240,247,255,0.95)" filter="url(#rv-glow)" />
        <circle cx="7" cy={bodyY + 19} r="1.5" fill="#36F4A4" />
      </g>

      {/* distance dimension */}
      <line x1={lensX} y1={floorY + 18} x2={wallX + 10} y2={floorY + 18} stroke="rgba(154,163,178,0.5)" strokeDasharray="3 4" />
      <line x1={lensX} y1={floorY + 13} x2={lensX} y2={floorY + 23} stroke="rgba(154,163,178,0.5)" />
      <line x1={wallX + 10} y1={floorY + 13} x2={wallX + 10} y2={floorY + 23} stroke="rgba(154,163,178,0.5)" />
      <text x={(lensX + wallX) / 2} y={floorY + 15} fill="#9AA3B2" fontSize="11" fontFamily="monospace" textAnchor="middle" data-testid="room-visual-distance">{distanceM.toFixed(1)} m</text>

      {/* labels */}
      <text x={wallX - 4} y={imgTop - 7} fill={fits ? "#dbe7f5" : "#D4A843"} fontSize="12" fontFamily="monospace" textAnchor="end" data-testid="room-visual-diag">{diag}″</text>
      <text x={wallX - 4} y={screenBot + 16} fill="#9AA3B2" fontSize="10" fontFamily="monospace" textAnchor="end">{screenInch}″ {t("simulator.screen")}</text>
      <text x={projX + 2} y={bodyY - 8} fill="#9AA3B2" fontSize="10" fontFamily="monospace">{t("simulator.projector")}</text>

      {/* fit chip */}
      <g data-testid="room-visual-fit-chip">
        <rect x="20" y="16" width={fits ? 106 : 128} height="22" rx="11" fill={fits ? "rgba(54,244,164,0.08)" : "rgba(212,168,67,0.1)"} stroke={fits ? "rgba(54,244,164,0.35)" : "rgba(212,168,67,0.4)"} strokeWidth="1" />
        <circle cx="34" cy="27" r="3" fill={fits ? "#36F4A4" : "#D4A843"} />
        <text x="44" y="31" fill={fits ? "#36F4A4" : "#D4A843"} fontSize="11" fontFamily="monospace">{fits ? t("simulator.goodFit") : t("simulator.tooBig")}</text>
      </g>
    </svg>
  );
};
